import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import useTenantPhoneCode from '@/hooks/useTenantPhoneCode';
import { COUNTRY_CODES_SORTED, splitPhoneValue } from '@/lib/countryCodes';

/**
 * A phone input with a country-code DROPDOWN plus a plain number field,
 * combined into a single string value ("+52 55 1234 5678") — the shape
 * every backend field already expects, so no API/model change needed.
 *
 * Default code priority (Sprint 57.6): `defaultCountryCode` prop (e.g. the
 * selected sucursal's own code, passed in by the caller) > the tenant's own
 * Tenant.phone_code (via useTenantPhoneCode) > '+52' fallback. The prop is
 * live — if the caller's branch selection changes AFTER this renders empty,
 * the effect below re-applies it.
 */
export default function PhoneInput({ value, onChange, defaultCountryCode, className, ...props }) {
  const tenantPhoneCode = useTenantPhoneCode();
  const effectiveDefault = defaultCountryCode || tenantPhoneCode;
  const prefilled = useRef(false);
  const { code, rest } = splitPhoneValue(value);
  const [manualCode, setManualCode] = useState(code);

  useEffect(() => {
    if (!prefilled.current && !value && effectiveDefault && onChange) {
      prefilled.current = true;
      setManualCode(effectiveDefault);
      onChange({ target: { value: effectiveDefault } });
    }
  }, [value, effectiveDefault, onChange]);

  // If the caller's default changes (e.g. picked a different sucursal)
  // before the user has typed a real number, follow it.
  useEffect(() => {
    if (effectiveDefault && (!rest || rest === '') && manualCode !== effectiveDefault) {
      setManualCode(effectiveDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDefault]);

  const handleCodeChange = (e) => {
    const newCode = e.target.value;
    setManualCode(newCode);
    onChange && onChange({ target: { value: `${newCode} ${rest}`.trim() } });
  };

  const handleRestChange = (e) => {
    const newRest = e.target.value;
    const activeCode = manualCode || code || effectiveDefault || '';
    onChange && onChange({ target: { value: `${activeCode} ${newRest}`.trim() } });
  };

  return (
    <div className="flex gap-1">
      <select
        value={manualCode || code || effectiveDefault || ''}
        onChange={handleCodeChange}
        className="border rounded px-1.5 py-1.5 text-sm bg-white flex-shrink-0 w-[92px]"
        data-testid="phone-country-code"
      >
        {!COUNTRY_CODES_SORTED.some(c => c.code === (manualCode || code)) && (manualCode || code) && (
          <option value={manualCode || code}>{manualCode || code}</option>
        )}
        {COUNTRY_CODES_SORTED.slice().sort((a, b) => a.country.localeCompare(b.country)).map(c => (
          <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
        ))}
      </select>
      <Input
        type="tel"
        value={rest}
        onChange={handleRestChange}
        className={className}
        {...props}
      />
    </div>
  );
}
